/* ostire-pdp.js — product detail page behaviour.

   Loaded as a module so it can import the theme's own event classes through
   the importmap in snippets/scripts.liquid. That matters for add-to-cart:
   Horizon's cart drawer listens for CartAddEvent, so dispatching the real
   class opens and refreshes the drawer instead of us guessing at a string.

   Everything degrades. With JS off the form still posts to /cart/add, the
   gallery still works because the thumbnails are CSS-driven radios, and the
   first available variant is preselected server-side. */

import { CartAddEvent } from '@theme/events';

function setup(root) {
  if (!root || root.dataset.pdpReady === '1') return;
  root.dataset.pdpReady = '1';

  const dataEl = root.querySelector('[data-pdp-variants]');
  if (!dataEl) return;

  let variants = [];
  try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

  const form = root.querySelector('[data-pdp-form]');
  const idInput = root.querySelector('[data-pdp-variant-id]');
  const priceEl = root.querySelector('[data-pdp-price]');
  const btn = root.querySelector('[data-pdp-add]');
  const btnLabel = root.querySelector('[data-pdp-add-label]');
  const stickyPrice = root.querySelector('[data-pdp-sticky-price]');
  const stickyImg = root.querySelector('[data-pdp-sticky-img]');
  const btnPrice = root.querySelector('[data-pdp-btn-price]');
  const stockEl = root.querySelector('[data-pdp-stock]');
  const inputs = Array.from(root.querySelectorAll('[data-option-index]'));
  const indicators = Array.from(root.querySelectorAll('[data-option-indicator]'));
  const soldOutText = root.dataset.soldOutText || 'Sold out';
  const addText = root.dataset.addText || 'Add to cart';
  const unavailableText = root.dataset.unavailableText || 'Unavailable';

  function selected() {
    const out = [];
    inputs.forEach(function (el) {
      if (el.checked) out[Number(el.dataset.optionIndex)] = el.value;
    });
    return out;
  }

  function match(opts) {
    return variants.find(function (v) {
      return opts.every(function (val, i) { return val === undefined || v.options[i] === val; });
    }) || null;
  }

  // A combination that does not exist is marked rather than hidden, so the
  // shopper can see what is gone instead of silently getting nothing.
  function markAvailability() {
    const current = selected();
    inputs.forEach(function (el) {
      const i = Number(el.dataset.optionIndex);
      const probe = current.slice();
      probe[i] = el.value;
      const exists = variants.some(function (v) {
        return probe.every(function (val, j) { return val === undefined || v.options[j] === val; }) && v.available;
      });
      const face = el.nextElementSibling;
      if (face) face.setAttribute('data-unavailable', exists ? 'false' : 'true');
    });
  }

  function showMedia(mediaId) {
    if (!mediaId) return;
    const radio = root.querySelector('[data-media-id="' + mediaId + '"]');
    if (radio && !radio.checked) radio.checked = true;
    const slide = root.querySelector('[data-slide-media="' + mediaId + '"]');
    const carousel = root.querySelector('[data-pdp-carousel]');
    if (slide && carousel) carousel.scrollTo({ left: slide.offsetLeft - carousel.offsetLeft, behavior: 'smooth' });
  }

  function update() {
    const variant = match(selected());

    indicators.forEach(function (el) {
      const i = Number(el.dataset.optionIndicator);
      const val = selected()[i];
      if (val) el.textContent = val;
    });

    markAvailability();

    if (!variant) {
      if (btnLabel) btnLabel.textContent = unavailableText;
      if (btnPrice) btnPrice.textContent = '';
      if (btn) btn.disabled = true;
      if (stockEl) stockEl.hidden = true;
      return;
    }

    // Low-stock note: driven by the real inventory count Shopify reports for
    // this exact variant, never by a made-up number.
    if (stockEl) {
      const limit = Number(stockEl.dataset.threshold || 0);
      const n = variant.stock;
      if (limit > 0 && variant.available && typeof n === 'number' && n > 0 && n <= limit) {
        stockEl.textContent = (stockEl.dataset.template || 'Only [count] left').replace('[count]', String(n));
        stockEl.hidden = false;
      } else {
        stockEl.hidden = true;
      }
    }
    if (btnPrice) btnPrice.textContent = variant.available ? '· ' + variant.price_plain : '';

    if (idInput) idInput.value = String(variant.id);
    if (priceEl) priceEl.innerHTML = variant.price_html;
    if (stickyPrice) stickyPrice.textContent = variant.price_plain;
    if (btn) btn.disabled = !variant.available;
    if (btnLabel) btnLabel.textContent = variant.available ? addText : soldOutText;
    if (variant.media) showMedia(variant.media);
    if (stickyImg && variant.media_src) stickyImg.src = variant.media_src;

    if (variant.id && window.history && history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }
  }

  inputs.forEach(function (el) { el.addEventListener('change', update); });

  // Size guide dialog
  const guide = root.querySelector('[data-pdp-guide]');
  if (guide) {
    root.querySelectorAll('[data-pdp-guide-open]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (typeof guide.showModal === 'function') guide.showModal();
        else guide.setAttribute('open', '');
      });
    });
    guide.querySelectorAll('[data-pdp-guide-close]').forEach(function (b) {
      b.addEventListener('click', function () { guide.close ? guide.close() : guide.removeAttribute('open'); });
    });
    guide.addEventListener('click', function (e) { if (e.target === guide && guide.close) guide.close(); });
  }

  // Quantity stepper
  const qtyInput = root.querySelector('[data-pdp-qty]');
  root.querySelectorAll('[data-qty-step]').forEach(function (b) {
    b.addEventListener('click', function () {
      if (!qtyInput) return;
      const step = Number(b.dataset.qtyStep);
      const next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + step);
      qtyInput.value = String(next);
    });
  });

  // Thumbnails drive the radios; clicking one also parks the carousel.
  root.querySelectorAll('[data-thumb-media]').forEach(function (label) {
    label.addEventListener('click', function () { showMedia(label.dataset.thumbMedia); });
  });

  // Carousel dots
  const carousel = root.querySelector('[data-pdp-carousel]');
  const dots = Array.from(root.querySelectorAll('[data-dot-index]'));
  if (carousel && dots.length) {
    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        const slide = carousel.children[Number(dot.dataset.dotIndex)];
        if (slide) carousel.scrollTo({ left: slide.offsetLeft - carousel.offsetLeft, behavior: 'smooth' });
      });
    });
    let raf = 0;
    carousel.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = 0;
        const i = Math.round(carousel.scrollLeft / Math.max(carousel.clientWidth, 1));
        dots.forEach(function (d, j) {
          if (j === i) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      });
    }, { passive: true });
  }

  // Sticky bar appears once the real button has scrolled away.
  const sticky = root.querySelector('[data-pdp-sticky]');
  if (sticky && btn && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        sticky.setAttribute('data-show', entry.isIntersecting ? 'false' : 'true');
      });
    }, { rootMargin: '0px 0px -20% 0px' });
    io.observe(btn);
  }

  // The sticky button adds the current selection straight to the cart
  // instead of only scrolling back up.
  const stickyBtn = root.querySelector('.os-pdp-stickybar-btn');
  if (stickyBtn && form) {
    stickyBtn.addEventListener('click', function (e) {
      if (btn && btn.disabled) return;
      e.preventDefault();
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else if (btn) btn.click();
    });
  }

  // Add to cart. Falls back to a normal navigation if anything at all fails,
  // so a shopper never silently loses an add.
  if (form) {
    form.addEventListener('submit', function (e) {
      // scripts.liquid declares `const Theme = {...}` in a classic script.
      // That is a lexical binding, so it is reachable by name but is NOT a
      // property of window — checking window.Theme silently disables this
      // whole path and drops every add back to a full page reload.
      const themeRoutes = (typeof Theme !== 'undefined' && Theme.routes) || null;
      const routes = themeRoutes || { cart_add_url: '/cart/add.js', cart_url: '/cart' };
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer || !window.fetch) return;

      e.preventDefault();
      if (btn) btn.disabled = true;

      const body = new FormData(form);

      // Horizon re-renders more than the drawer on a cart add: the header
      // carries the cart count bubble, and its listener throws if the id it
      // wants is missing from the response. Ask for every section that
      // listens, not just the drawer.
      const ids = [];
      const add = function (el) {
        const sec = el && el.closest('[id^="shopify-section-"]');
        const id = sec ? sec.id.replace('shopify-section-', '') : null;
        if (id && ids.indexOf(id) === -1) ids.push(id);
      };
      add(drawer);
      add(document.querySelector('#header-component'));
      add(document.querySelector('cart-icon-component'));
      if (ids.length) body.append('sections', ids.join(','));

      fetch(routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json' }, body: body })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.json.description || 'add failed');
          const sections = res.json.sections || null;
          return fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.dispatchEvent(new CartAddEvent(cart, root.id, {
                source: 'ostire-pdp',
                itemCount: cart.item_count,
                variantId: idInput ? idInput.value : undefined,
                sections: sections
              }));
              // The drawer only auto-opens when it carries an `auto-open`
              // attribute, which it does not here, so the add would otherwise
              // succeed with no visible feedback at all. Open it ourselves.
              if (typeof drawer.open === 'function') drawer.open();
            });
        })
        .catch(function () {
          form.submit();
        })
        .finally(function () {
          if (btn) btn.disabled = false;
        });
    });
  }

  update();
}

function scan(scope) {
  (scope || document).querySelectorAll('[data-os-pdp]').forEach(setup);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
else scan();

document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
